import { useState, type FormEvent } from 'react';
import type { AudienceScope, MessageKey } from '@hamro/shared';
import { useT, useLocale } from '../lib/i18n.js';
import { useSession } from '../lib/session.js';
import { useCreateNotice, useNoticeTargets, useNotices } from '../lib/queries.js';
import { ApiRequestError } from '../lib/api.js';
import { PageHeader } from '../components/PageHeader.js';
import { Panel } from '../components/Panel.js';
import { EmptyState } from '../components/EmptyState.js';
import { QueryState } from '../components/QueryState.js';
import { StatusPill } from '../components/StatusPill.js';
import { Toast } from '../components/Toast.js';
import { instant } from '../lib/format.js';

/**
 * Notices from the office.
 *
 * The reader only ever receives the ones they are an audience for — the server
 * decides that from the notice's scope and its `audienceRoles`, so a parent in
 * 7B never has 8A's trip letter in the response to filter out.
 */
export default function NoticesPage() {
  const t = useT();
  const locale = useLocale();
  const { user, can } = useSession();
  const notices = useNotices();
  const timezone = user?.school.timezone ?? 'UTC';
  const [toast, setToast] = useState<{ kicker: string; detail: string; tone: 'ink' | 'stamp' } | null>(
    null,
  );

  return (
    <>
      <PageHeader title={t('page.notices.title')} subtitle={t('page.notices.subtitle')} />

      {can('notice:write') && <Compose onResult={setToast} />}

      <QueryState
        isLoading={notices.isLoading}
        error={notices.error}
        onRetry={() => void notices.refetch()}
      >
        {(notices.data ?? []).length === 0 ? (
          <Panel>
            <EmptyState
              message={can('notice:write') ? 'page.notices.empty' : 'page.notices.empty_reader'}
            />
          </Panel>
        ) : (
          <div className="grid gap-3">
            {(notices.data ?? []).map((notice) => (
              <Panel key={notice.id}>
                <div className="px-4 py-3.5 sm:px-5">
                  <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
                    {notice.isPinned && (
                      <StatusPill label={t('page.notices.pinned')} tone="marigold" />
                    )}
                    <h2 className="font-display text-[16px] font-bold">{notice.title}</h2>
                    <span className="ml-auto font-mono text-[10.5px] text-ink-45">
                      {notice.publishedAt ? instant(notice.publishedAt, timezone, locale) : ''}
                    </span>
                  </div>

                  {/* School-authored copy. Stored and shown verbatim — it is
                      data, not a catalogue string. */}
                  <p className="mt-1.5 text-[14.5px] whitespace-pre-line text-ink-70">
                    {notice.body}
                  </p>

                  <div className="mt-2.5 flex flex-wrap items-center gap-2 font-mono text-[10.5px] text-ink-45">
                    <span>
                      {notice.scope === 'SCHOOL'
                        ? t('page.notices.audience_school')
                        : (notice.audienceName ?? '')}
                    </span>
                    <span aria-hidden>·</span>
                    <span>{notice.authorName}</span>
                  </div>
                </div>
              </Panel>
            ))}
          </div>
        )}
      </QueryState>

      {toast && (
        <Toast
          kicker={toast.kicker}
          detail={toast.detail}
          tone={toast.tone}
          onDismiss={() => setToast(null)}
        />
      )}
    </>
  );
}

type Notify = (toast: { kicker: string; detail: string; tone: 'ink' | 'stamp' }) => void;

/**
 * Posting a notice.
 *
 * The audience choices are the writer's, not the schema's: the office gets the
 * whole school, a grade or one class, and a class teacher gets only the classes
 * they hold. `canPostSchoolWide` comes from the server, so the option a teacher
 * cannot use is absent rather than present-and-refused.
 */
function Compose({ onResult }: { onResult: Notify }) {
  const t = useT();
  const targets = useNoticeTargets(true);
  const create = useCreateNotice();

  const [scope, setScope] = useState<AudienceScope | ''>('');
  const [gradeLevelId, setGradeLevelId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [isPinned, setIsPinned] = useState(false);

  if (targets.isLoading) return null;

  const data = targets.data;
  if (!data) return null;

  // A teacher with no classes this year has nobody to address.
  if (!data.canPostSchoolWide && data.sections.length === 0) return null;

  const scopes: AudienceScope[] = [
    ...(data.canPostSchoolWide ? (['SCHOOL', 'GRADE_LEVEL'] as AudienceScope[]) : []),
    'SECTION',
  ];
  const effectiveScope = (scope || scopes[0]) as AudienceScope;
  const effectiveGrade = gradeLevelId || data.gradeLevels[0]?.id || '';
  const effectiveSection = sectionId || data.sections[0]?.id || '';

  const audienceLabel =
    effectiveScope === 'SCHOOL'
      ? t('compose.notice.audience_school')
      : effectiveScope === 'GRADE_LEVEL'
        ? (data.gradeLevels.find((grade) => grade.id === effectiveGrade)?.name ?? '')
        : (data.sections.find((section) => section.id === effectiveSection)?.name ?? '');

  async function submit(event: FormEvent) {
    event.preventDefault();
    try {
      await create.mutateAsync({
        title: title.trim(),
        body: body.trim(),
        scope: effectiveScope,
        gradeLevelId: effectiveScope === 'GRADE_LEVEL' ? effectiveGrade : undefined,
        sectionId: effectiveScope === 'SECTION' ? effectiveSection : undefined,
        isPinned,
      });
      onResult({
        kicker: t('compose.notice.done'),
        detail: t('compose.notice.done_detail', { audience: audienceLabel }),
        tone: 'ink',
      });
      setTitle('');
      setBody('');
      setIsPinned(false);
    } catch (error) {
      onResult({
        kicker: t('error.generic'),
        detail: error instanceof ApiRequestError ? t(error.key as MessageKey) : t('error.generic'),
        tone: 'stamp',
      });
    }
  }

  const scopeLabel = (value: AudienceScope): string =>
    value === 'SCHOOL'
      ? t('compose.notice.audience_school')
      : value === 'GRADE_LEVEL'
        ? t('compose.notice.audience_grade')
        : t('compose.notice.audience_section');

  return (
    <div className="mb-4">
      <Panel title={t('compose.notice.title')}>
        <form className="grid gap-3 px-4 py-3.5 sm:px-5" onSubmit={submit}>
          <div className="flex flex-wrap gap-2">
            <label className="min-w-[150px] flex-1">
              <span className="field-label mb-1 block">{t('compose.notice.audience')}</span>
              <select
                className="input"
                value={effectiveScope}
                onChange={(event) => setScope(event.target.value as AudienceScope)}
              >
                {scopes.map((value) => (
                  <option key={value} value={value}>
                    {scopeLabel(value)}
                  </option>
                ))}
              </select>
            </label>

            {effectiveScope === 'GRADE_LEVEL' && (
              <label className="min-w-[150px] flex-1">
                <span className="field-label mb-1 block">
                  {t('compose.notice.audience_grade')}
                </span>
                <select
                  className="input"
                  value={effectiveGrade}
                  onChange={(event) => setGradeLevelId(event.target.value)}
                >
                  {data.gradeLevels.map((grade) => (
                    <option key={grade.id} value={grade.id}>
                      {grade.name}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {effectiveScope === 'SECTION' && (
              <label className="min-w-[150px] flex-1">
                <span className="field-label mb-1 block">
                  {t('compose.notice.audience_section')}
                </span>
                <select
                  className="input"
                  value={effectiveSection}
                  onChange={(event) => setSectionId(event.target.value)}
                >
                  {data.sections.map((section) => (
                    <option key={section.id} value={section.id}>
                      {section.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          <label>
            <span className="field-label mb-1 block">{t('compose.notice.heading')}</span>
            <input
              className="input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </label>

          <label>
            <span className="field-label mb-1 block">{t('compose.notice.body')}</span>
            <textarea
              className="input min-h-[76px] resize-y"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              required
            />
          </label>

          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-[13.5px] text-ink-70">
              <input
                type="checkbox"
                className="size-4 accent-ink"
                checked={isPinned}
                onChange={(e) => setIsPinned(e.target.checked)}
              />
              {t('compose.notice.pin')}
            </label>
            <button
              type="submit"
              className="btn-primary ml-auto"
              disabled={create.isPending || !title.trim() || !body.trim()}
            >
              {create.isPending ? t('action.posting_notice') : t('action.post_notice')}
            </button>
          </div>
        </form>
      </Panel>
    </div>
  );
}
